import { createRouter } from "@tanstack/solid-router";
import { NotFoundRoute } from "@/components/not-found-route";
import { rootRoute } from "@/routes/__root";
import { authRoute } from "@/routes/_auth/route";
import { loginRoute } from "@/routes/_auth/login";
import { registerRoute } from "@/routes/_auth/register";
import { forgotPasswordRoute } from "@/routes/_auth/forgot-password";
import { resetPasswordRoute } from "@/routes/_auth/reset-password";
import { verifyEmailRoute } from "@/routes/_auth/verify-email";
import { socialCallbackRoute } from "@/routes/_auth/social-callback";
import { appRoute } from "@/routes/_app/route";
import { indexRoute } from "@/routes/_app/index";
import { profileRoute } from "@/routes/_app/profile";
// §M1 · "My profiles": what I am in each academy and what each profile unlocks.
import { profilesRoute } from "@/routes/_app/profiles";
import { debugRoute } from "@/routes/_app/debug";
import { organizationsRoute } from "@/routes/_app/organizations";
import { cc21ProbeRoute } from "@/routes/_app/cc21-probe";
import { organizationDetailRoute } from "@/routes/_app/organizations.$orgId";
import { invitationsRoute } from "@/routes/_app/invitations";
import { themeGalleryRoute } from "@/routes/_app/themes";
import { languageRoute } from "@/routes/_app/language";
import { profileFontsRoute } from "@/routes/_app/profile.fonts";
import { profilePlanRoute } from "@/routes/_app/profile.plan";
import { profileShortcutsRoute } from "@/routes/_app/profile.shortcuts";
import { profileDeleteAccountRoute } from "@/routes/_app/profile.delete-account";
import { profileTransferSendRoute } from "@/routes/_app/profile.transfer-send";
import { profileTransferReceiveRoute } from "@/routes/_app/profile.transfer-receive";
// SPACE · courses domain
import { catalogRoute } from "@/routes/_app/catalog";
import { catalogCourseRoute } from "@/routes/_app/catalog.$courseId";
import { learnRoute } from "@/routes/_app/learn";
import { learnCourseRoute } from "@/routes/_app/learn.$courseId";
import { leaderboardRoute } from "@/routes/_app/leaderboard";
import { authoringRoute } from "@/routes/_app/authoring";
import { authoringCourseRoute } from "@/routes/_app/authoring.$courseId";
import { certificateRoute } from "@/routes/_app/certificates.$certId";
import { verifyCertificateRoute } from "@/routes/verify.$certId";
// P41 · guardian consent: PUBLIC page for the double opt-in.
import { guardianConsentRoute } from "@/routes/guardian-consent";
// P41 · "Who represents me": the half that was missing for the student.
import { profileGuardianshipRoute } from "@/routes/_app/profile.guardianship";
// ACADEMY · F4-C: academy analytics dashboard (index)
import { academyIndexRoute } from "@/routes/_app/academy.index";
// ACADEMY · 1C: STUDENT classroom (index of their cohorts + one cohort's landing)
import { classroomIndexRoute } from "@/routes/_app/classroom";
import { classroomDetailRoute } from "@/routes/_app/classroom.$cohortId";
// ACADEMY · F2-A: cohorts + programs
import { academyCohortsRoute } from "@/routes/_app/academy.cohorts";
import { academyTutoringRoute } from "@/routes/_app/academy.tutoring";
// ACADEMY · B1: entitlement (what happens if the student hasn't paid)
import { academyEntitlementRoute } from "@/routes/_app/academy.entitlement";
import { academyCohortDetailRoute } from "@/routes/_app/academy.cohorts.$cohortId";
import { academyProgramsRoute } from "@/routes/_app/academy.programs";
import { academyProgramDetailRoute } from "@/routes/_app/academy.programs.$programId";
// ACADEMY · F3-D: people (directory + record + teacher profile + self-view)
import { academyPeopleRoute } from "@/routes/_app/academy.people";
// F3-C: notifications + announcements (forums + comms + badges)
import { notificationsRoute } from "@/routes/_app/notifications";
import { academyAnnouncementsRoute } from "@/routes/_app/academy.announcements";
import { academyPersonDetailRoute } from "@/routes/_app/academy.people.$userId";
import { academyTeacherEditorRoute } from "@/routes/_app/academy.teachers.$userId";
import { profileAcademicRoute } from "@/routes/_app/profile.academic";
// ACADEMY · F3-D: teacher PUBLIC page (guest-first, outside /u/<n>)
import { publicTeacherRoute } from "@/routes/teachers.$orgSlug.$slug";
// ACADEMY · F3-H: billing (cashier/invoices/rates/receipt, admin verification, student)
import { aiTutorRoute } from "@/routes/_app/ai-tutor";
import { crmRoute } from "@/routes/_app/crm";
import { crmSegmentsRoute } from "@/routes/_app/crm.segments";
import { crmTemplatesRoute } from "@/routes/_app/crm.templates";
import { leadDetailRoute } from "@/routes/_app/crm.leads.$leadId";
import { financeRoute } from "@/routes/_app/finance";
import { financePaymentsRoute } from "@/routes/_app/finance.payments";
import { financeInvoicesRoute } from "@/routes/_app/finance.invoices";
import { financeRatesRoute } from "@/routes/_app/finance.rates";
import { receiptRoute } from "@/routes/_app/finance.receipt.$paymentId";
import { paymentsRoute } from "@/routes/_app/payments";
// ACADEMY · F3-A: personal schedule, check-in and campus occupancy
import { scheduleRoute } from "@/routes/_app/schedule";
import { sessionPlanRoute } from "@/routes/_app/schedule.$sessionId";
// F4-A: traceability (activity feed + trash).
import { pluginRoutes } from "@/composition";
import { trashRoute } from "@/routes/_app/trash";
import { checkinRoute } from "@/routes/_app/checkin";
// F3-G: live classes (access screen / Jitsi embed / recording)
import { liveSessionRoute } from "@/routes/_app/live.$sessionId";
import { academyCampusOccupancyRoute } from "@/routes/_app/academy.campuses.$campusId";
// ACADEMY · F3-B: student assignments page
import { assignmentsRoute } from "@/routes/_app/assignments";
// B6 · levels: the student's path (gated by academy.progression.path).
import { pathRoute } from "@/routes/_app/path";
// M2: final project (index + per-cohort detail).
import { finalProjectIndexRoute } from "@/routes/_app/final-project";
import { finalProjectDetailRoute } from "@/routes/_app/final-project.$cohortId";
// ── AUTONOMY: in-app help center (role guides + contextual help) ──
import { helpRoute } from "@/routes/_app/help";
import { helpGuideRoute } from "@/routes/_app/help.$guideSlug";
// AUTONOMY: billing configuration reachable by org_admin (no platform role).
import { financeSettingsRoute } from "@/routes/_app/finance.settings";
// ── end AUTONOMY ──

const routeTree = rootRoute.addChildren([
  socialCallbackRoute,
  // PUBLIC certificate verification (guest-first, outside /u/<n>).
  verifyCertificateRoute,
  // F3-D · teacher PUBLIC page (guest-first, outside /u/<n>).
  publicTeacherRoute,
  // P41 · the guardian opens this link from the email, without a session.
  guardianConsentRoute,
  authRoute.addChildren([
    loginRoute,
    registerRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyEmailRoute,
  ]),
  appRoute.addChildren([
    // CC-21 regression fixture (Select/Popover inside a Dialog). DEV only.
    ...(import.meta.env.DEV ? [cc21ProbeRoute] : []),
    indexRoute, // path: "/" — redirects to /learn
    // SPACE · courses
    catalogRoute,
    catalogCourseRoute,
    learnRoute,
    learnCourseRoute,
    leaderboardRoute,
    authoringRoute,
    authoringCourseRoute,
    certificateRoute,
    // ACADEMY · F4-C: analytics dashboard
    academyIndexRoute,
    // ACADEMY · 1C: student classroom
    classroomIndexRoute,
    classroomDetailRoute,
    // ACADEMY · F2-A: cohorts + programs
    academyCohortsRoute,
    academyCohortDetailRoute,
    // ACADEMY · M3: tutoring panel (grading queue + resubmission)
    academyTutoringRoute,
    // ACADEMY · B1: entitlement (non-payment policy + exceptions + simulation)
    academyEntitlementRoute,
    academyProgramsRoute,
    academyProgramDetailRoute,
    // ACADEMY · F3-D: people + record + teacher profile + self-view
    academyPeopleRoute,
    notificationsRoute,
    academyAnnouncementsRoute,
    academyPersonDetailRoute,
    academyTeacherEditorRoute,
    profileAcademicRoute,
    // F3-I: CRM / lead capture (kanban + segments + templates + lead detail)
    aiTutorRoute,
    crmRoute,
    crmSegmentsRoute,
    crmTemplatesRoute,
    leadDetailRoute,
    // F3-H: academy billing
    financeRoute,
    financePaymentsRoute,
    financeInvoicesRoute,
    financeRatesRoute,
    receiptRoute,
    paymentsRoute,
    // ACADEMY · F3-A: schedule / check-in / occupancy
    scheduleRoute,
    // ACADEMY · M5: session lesson plan (flipped classroom)
    sessionPlanRoute,
    ...pluginRoutes,
    trashRoute,
    checkinRoute,
    // F3-G: live classes
    liveSessionRoute,
    academyCampusOccupancyRoute,
    // F3-B: student assignments
    assignmentsRoute,
    pathRoute,
    finalProjectIndexRoute,
    finalProjectDetailRoute,
    // ── AUTONOMY: in-app help center + org-side billing config ──
    helpRoute,
    helpGuideRoute,
    financeSettingsRoute,
    // ── end AUTONOMY ──
    organizationsRoute,
    organizationDetailRoute,
    invitationsRoute,
    themeGalleryRoute,
    languageRoute,
    profileRoute,
    profilesRoute,
    profileFontsRoute,
    profilePlanRoute,
    profileShortcutsRoute,
    profileDeleteAccountRoute,
    profileGuardianshipRoute,
    profileTransferSendRoute,
    profileTransferReceiveRoute,
    debugRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  // oxlint-disable-next-line eslint(no-magic-numbers)
  defaultPreloadStaleTime: 30_000,
  defaultNotFoundComponent: NotFoundRoute,
});

declare module "@tanstack/solid-router" {
  interface Register {
    router: typeof router;
  }
}
